import { Injectable, inject } from '@angular/core';
import { ListResponse } from '@models/helpers';
import { FileItemModel } from '@models/module/fields/main';
import { HttpService } from '@shared/services';

export type FolderFileRequestParams = {
  isFolders: boolean;
  /** Contract entity instance ID. Required only for _api/ProjectFolderFile requests
   * In fact, based on the presence of this parameter, we decide whether to call
   * _api/ProjectFolderFile or _api/FolderFile endpoints
   */
  entityInstanceId?: number;
  /** Project Files only: module of the root context entity (Contract/Project/Program). */
  rootModuleId?: number;
  criteria: string;
  destinationFolderId: number;
  files: { id: number; note?: string }[];
  /** For ProjectFolderFile root, this expects the entityInstanceId (of the contract) */
  id: number;
  isGlobal: boolean;
  parentId: number;
  name: string;
};

/** This is the main service for folder and file operations */
@Injectable({
  providedIn: 'root',
})
export class FolderFileService {
  private http = inject(HttpService);

  private getControllerPrefix(isProjectFolder?: boolean) {
    return isProjectFolder ? '_api/ProjectFolderFile' : '_api/FolderFile';
  }

  search({
    entityInstanceId,
    id,
    isFolders,
    rootModuleId,
    isGlobal,
  }: Pick<FolderFileRequestParams, 'entityInstanceId' | 'isFolders' | 'id' | 'rootModuleId'> &
    Partial<Pick<FolderFileRequestParams, 'isGlobal'>>) {
    const global = !!isGlobal && !!entityInstanceId;
    const params = this.http.buildHttpParams({
      isFolders,
      ...(entityInstanceId ? { entityInstanceId } : {}),
      ...(entityInstanceId && rootModuleId ? { rootModuleId } : {}),
    });
    return this.http.post<ListResponse<FileItemModel>>(
      `${this.getControllerPrefix(!!entityInstanceId)}/${global ? 'SearchGlobal' : 'Search'}`,
      { id, ...(entityInstanceId ? { entityInstanceId } : {}), ...(global ? { isGlobal: true } : {}) },
      { httpOptions: { params }, useLoader: false },
    );
  }

  saveAttachmentsToFolder({
    entityInstanceId,
    ...props
  }: Pick<FolderFileRequestParams, 'entityInstanceId' | 'destinationFolderId' | 'files' | 'destinationFolderId'>) {
    const params = this.http.buildHttpParams({ ...(entityInstanceId ? { entityInstanceId } : {}) });
    return this.http.put<void>(`${this.getControllerPrefix(!!entityInstanceId)}/SaveAttachmentsToFolder`, props, {
      httpOptions: { params },
      useLoader: false,
    });
  }

  createFolder(body: Pick<FolderFileRequestParams, 'name' | 'parentId'>) {
    return this.http.post<number>('_api/FolderFile/CreateFolder', { ...body, isFolder: true }, { useLoader: false });
  }
}
